<template>
  <div id="lom-events-list">
    <v-overlay :value="DeleteLoadingFlag">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!-- Dialog Components -->
    <v-dialog
      v-model="AddRecordPrompt"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      persistent
    >
      <v-card>
        <lom-training-question-create
          :showDialog="AddRecordPrompt"
          PageTitle="LDMT/LAV Training Question Create"
          @hideDialog="hideAddRecordPrompt($event)"
          :Category="selectedData"
          v-if="AddRecordPrompt"
          :YearCode="JciYearCode"
        ></lom-training-question-create>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="editRecordPrompt"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      persistent
    >
      <v-card>
        <lom-training-question-edit
          :showDialog="editRecordPrompt"
          PageTitle="LDMT/LAV Training Question Edit"
          @hideDialog="hideEditRecordPrompt($event)"
          :recordData="selectedData"
          :YearCode="JciYearCode"
          v-if="editRecordPrompt"
        ></lom-training-question-edit>
      </v-card>
    </v-dialog>
    <!-- Premium Header Card with SpeechCraft Animation -->
    <v-card elevation="20" class="header-card mb-6 overflow-hidden">
      <div class="header-gradient">
        <div class="header-backdrop-effect"></div>
        <div class="header-content pa-8">
          <v-row align="center" justify="space-between">
            <!-- Left Side: Title and Icon -->
            <v-col cols="12" md="8" class="d-flex align-center">
              <div class="header-icon-box">
                <v-icon size="40px" color="white">mdi-file-document</v-icon>
              </div>
              <div class="ml-4">
                <h1 class="header-title white--text mb-0">{{ PageTitle }}</h1>
                <p class="header-subtitle white--text mb-0">
                  Manage and organize all training questions
                </p>
              </div>
            </v-col>

            <!-- Right Side: Action Buttons -->
            <v-col cols="12" md="4" class="text-right">
              <v-btn
                icon
                class="action-btn"
                @click="refreshPageData"
                title="Refresh"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
              <v-btn
                icon
                class="action-btn"
                @click="AddRecordPrompt = true"
                title="Add New Question"
              >
                <v-icon>mdi-plus-circle</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-card>

    <!-- Filter & Search Card -->
    <v-card elevation="12" class="filter-card mb-6">
      <v-container class="pa-6">
        <v-row wrap>
          <v-col cols="12" sm="6" md="3">
            <div class="form-field-wrapper">
              <label class="form-label">
                <v-icon small color="#2B9EB3">mdi-calendar</v-icon>
                <span class="text-danger">*</span> Year
              </label>
              <v-autocomplete
                :reduce="(option) => option.value"
                :loading="JciYearCodeOptionsLoading"
                :items="JciYearCodeOptions"
                :rules="JciYearCodeRules"
                v-model="JciYearCode"
                dense
                clearable
                required
                outlined
                placeholder="Select year"
                class="custom-input"
              >
                <template v-slot:prepend>
                  <v-icon color="#2B9EB3">mdi-calendar-check</v-icon>
                </template>
              </v-autocomplete>
            </div>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <div class="form-field-wrapper">
              <label class="form-label">
                <v-icon small color="#2B9EB3">mdi-folder</v-icon>
                <span class="text-danger">*</span> Category
              </label>
              <v-autocomplete
                :reduce="(option) => option.value"
                :items="CategoryOptions"
                :rules="CategoryRules"
                :loading="CategoryOptionsLoading"
                v-model="Category"
                @change="searchForm()"
                dense
                clearable
                required
                outlined
                placeholder="Select category"
                class="custom-input"
              >
                <template v-slot:prepend>
                  <v-icon color="#2B9EB3">mdi-folder-open</v-icon>
                </template>
              </v-autocomplete>
            </div>
          </v-col>

          <v-col cols="12" sm="12" md="6" class="d-flex align-end">
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              label="Search questions"
              single-line
              hide-details
              dense
              outlined
              class="search-field"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-container>
    </v-card>
    <!-- Loading State -->
    <div class="d-flex justify-center mb-4" v-if="LoadingFlag">
      <v-progress-circular indeterminate color="#0097d7"></v-progress-circular>
    </div>

    <!-- Data Table Card -->
    <v-card elevation="12" class="table-card" v-if="tableData1.length > 0">
      <v-container class="pa-6">
        <v-row class="mb-4">
          <v-col cols="12" class="d-flex align-center justify-space-between">
            <div>
              <h3 class="table-title">
                <v-icon class="mr-2">mdi-file-document-list-multiple</v-icon>
                Training Questions ({{ tableData1.length }} Records)
              </h3>
            </div>
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              label="Search in table"
              single-line
              hide-details
              dense
              outlined
              class="search-field"
              style="max-width: 300px"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-data-table
          class="questions-table"
          v-model="selected"
          :headers="tableColumns1"
          :items="tableData1"
          :single-select="tableOptions1.SingleSelectFlag"
          :search="search"
          :footer-props="{ itemsPerPageOptions: [10, 25, 50, 100, -1] }"
          :item-key="tableOptions1.ItemKey"
          multi-sort
        >
          <!-- Status Column -->
          <template v-slot:item.ActiveStatusTxt="{ item }">
            <v-chip
              :color="item.ActiveStatusTxt === 'Active' ? '#4caf50' : '#f44336'"
              text-color="white"
              small
              class="font-weight-bold"
            >
              <v-icon left small>{{
                item.ActiveStatusTxt === "Active"
                  ? "mdi-check-circle"
                  : "mdi-close-circle"
              }}</v-icon>
              {{ item.ActiveStatusTxt }}
            </v-chip>
          </template>

          <!-- Actions Column -->
          <template v-slot:item.actions="{ item }">
            <div class="action-cell">
              <v-btn
                x-small
                icon
                color="primary"
                @click="editData(item)"
                title="Edit"
                class="action-icon-btn"
              >
                <v-icon small>mdi-pencil-circle</v-icon>
              </v-btn>
              <v-btn
                x-small
                icon
                color="error"
                @click="deleteConfirmAlert(item)"
                title="Delete"
                class="action-icon-btn"
              >
                <v-icon small>mdi-trash-can-outline</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-container>
    </v-card>

    <!-- Empty State -->
    <v-card elevation="12" class="empty-state-card" v-else-if="!LoadingFlag">
      <v-container class="pa-12 text-center">
        <v-icon size="80" color="grey lighten-2">mdi-inbox-outline</v-icon>
        <h3 class="mt-4 text-h6 grey--text text--darken-2">No records found</h3>
        <p class="mt-2 text-body2 grey--text">
          Select a year and category to view training questions
        </p>
      </v-container>
    </v-card>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import companyConfig from "@/company_config.json";
import {
  ADD_BODY_CLASSNAME,
  REMOVE_BODY_CLASSNAME,
} from "@/core/services/store/htmlclass.module.js";
import Swal from "sweetalert2";
import EventsPreview from "@/view/pages/erp/events/EventsPreview.vue";
import LomTrainingQuestionCreate from "@/view/pages/erp/events/lom-training-question/LomTrainingQuestionCreate.vue";
import LomTrainingQuestionEdit from "@/view/pages/erp/events/lom-training-question/LomTrainingQuestionEdit.vue";

export default {
  mixins: [common],
  components: {
    EventsPreview,
    LomTrainingQuestionCreate,
    LomTrainingQuestionEdit,
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      SearchFlag: false,
      SubmitFlag: false,
      SpeedDialFlag: false,
      TableLoadingFlag: false,
      DeleteLoadingFlag: false,

      JciYearCode: "",
      JciYearCodeOptions: [],
      JciYearCodeOptionsLoading: false,
      JciYearCodeRules: [],

      ExcelDownloadFlag: false,
      ExcelLoadingFlag: false,
      ExcelFileFlag: false,

      Category: "",
      CategoryOptions: [
        { value: "", text: "--Select Category--" },
        { value: 1, text: "First half visit" },
        { value: 2, text: "Second half visit" },
      ],
      CategoryOptionsLoading: false,
      CategoryRules: [],

      rows: [],

      Modules: {},

      tableData1: [],
      tableColumns1: [],
      tableOptions1: [],
      selected: [],
      search: "",
      CurrentYearId: 0,

      DownloadFlag: 0,
      ExcelFileName: "",
      excel_fields: {
        "LO Id": "LomId",
        "Zone Id": "ZoneId",
        "LO Name": "LomName",
        "President Name": "LomPresidentFullName",
        "LO NAME": "LomName",
        "Mobile No": "LomPresidentMobileNo",
        "Email Id": "LomPresidentEmailid",
      },

      recordToEdit: "",
      selectedData: {},
      record: {},
      AddRecordPrompt: false,
      editRecordPrompt: false,
      CreateFlag: false,
      previewRecordPrompt: false,
      PageDescription: "Assign LO President for JCI India",

      rowData: [],

      direction: "top",
      fab: true,
      fling: false,
      hover: true,
      tabs: null,
      top: false,
      right: true,
      bottom: true,
      left: false,
      transition: "slide-y-reverse-transition",

      ZoneCode: "",
      ZoneName: "",

      LomCode: "",
      LomName: "",

      YearCode: "",
      MonthCode: "",
      MonthName: "",

      PageInfo: {},
      PageTitle: "LO Training Question List",
      PageDescription: "",
      JciYearCode: "",
      YearName: "",
    };
  },
  mounted() {
    /*
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Vuetify", route: "alerts" },
      { title: "Form Inputs & Control", route: "autocompletes" },
      { title: "Fileinptus" },
    ]);
    */
  },
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;
        this.JciYearCode = tr.CurrentJciYearId;
        var YearName = tr.CurrentJciYearName;
        this.YearName = YearName;

        var MonthCode = this.$route.query.month;
        MonthCode = MonthCode == (null || undefined) ? 0 : MonthCode;
        console.log({ MonthCode });
        this.MonthCode = MonthCode;

        var tempDate1 = YearName + "-" + MonthCode + "-01";
        console.log({ tempDate1 });

        var tempDate2 = new Date(tempDate1);
        console.log({ tempDate2 });
        var MonthName = tempDate2.toLocaleString("en-us", { month: "long" });
        console.log({ MonthName });
        this.MonthName = MonthName;

        this.getYearCodeOptions();
        this.JciYearCodeOptionsLoading = true;

        this.searchForm();
      }
    },
    Category: function () {
      console.log("watch Category");
      this.CreateFlag = false;
      var temp_code = this.Category;
      console.log({ temp_code });
      var idx = this.CategoryOptions.map((e) => e.value).indexOf(temp_code);
      console.log("idx=" + idx);
      if (idx >= 0) {
        var Category = this.CategoryOptions[idx];
        console.log({ Category });
        this.selectedData = Category;
        this.CreateFlag = true;
      }
    },
    JciYearCodeOptions: function () {
      console.log("watch JciYearCodeOptions");
      this.JciYearCodeOptionsLoading = false;
    },
  },
  methods: {
    backPage() {
      this.$router.go(-1);
    },
    refreshPageData() {
      console.log("refreshPageData called");

      var ZoneName = this.$session.get("ZoneName");
      ZoneName = ZoneName == (null || undefined) ? "" : ZoneName;
      console.log("ZoneName=" + ZoneName);
      this.ZoneName = ZoneName;

      var ZoneCode = this.$session.get("ZoneId");
      ZoneCode = ZoneCode == (null || undefined) ? "" : ZoneCode;
      console.log("ZoneCode=" + ZoneCode);
      this.ZoneCode = ZoneCode;

      var LomCode = this.$session.get("LomId");
      LomCode = LomCode == (null || undefined) ? "" : LomCode;
      console.log("LomCode=" + LomCode);
      this.LomCode = LomCode;

      var LomName = this.$session.get("LomName");
      LomName = LomName == (null || undefined) ? "" : LomName;
      console.log("LomName=" + LomName);
      this.LomName = LomName;

      this.LoadingFlag = true;

      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "lo_training_question",
        Action: "list",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    validate() {
      if (this.$refs.form2.validate()) {
        this.snackbar = true;
      }
    },
    addRecord() {
      console.log("addRecord called");
      var url = "/lom/create";
      var params = {};
      this.pageRedirect(params, url);
    },
    resetForm() {
      this.$refs.form2.reset();
      this.tableData1 = [];
    },
    resetValidation() {
      this.$refs.form2.resetValidation();
    },
    addData(tr) {
      // console.log("addData called");
      // var n1 = Object.keys(tr).length;
      // console.log({ "n1":n1 });
      // console.log({ "tr":tr });
      // if (n1 > 0) {
      //   this.rowData = tr;
      this.AddRecordPrompt = true;
      // } else {
      //   var message = "Kindly select one settings to create";
      //   this.toast("error", message);
      // }
    },
    hideAddRecordPrompt() {
      console.log("hideAddRecordPrompt called");
      this.AddRecordPrompt = false;
      this.searchForm();
    },
    hideEditRecordPrompt(flag) {
      console.log("hideEditRecordPrompt called");
      console.log({ flag });
      this.editRecordPrompt = false;
      if (flag) {
        this.searchForm();
      }
    },
    editData(tr) {
      console.log("editData called");
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      console.log({ tr });
      if (n1 > 0) {
        this.selectedData = tr;
        this.editRecordPrompt = true;
      } else {
        var message = "Kindly select one settings to edit";
        this.toast("error", message);
      }
    },
    hidePreviewRecordPrompt() {
      console.log("hidePreviewRecordPrompt called");
      this.previewRecordPrompt = false;
    },
    previewData(tr) {
      console.log("previewData called");
      // var tr = this.selected
      // var n1 = tr.length
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.previewRecordPrompt = true;
        this.selectedData = tr;
      } else {
        var message = "Kindly select one record to preview";
        this.toast("error", message);
      }
    },
    getYearCodeOptions() {
      console.log("getYearCodeOptions called");
      var CurrentYearId = this.CurrentYearId;
      this.AreaCode = "";
      console.log("CurrentYearId=" + CurrentYearId);
      this.JciYearCodeOptionsLoading = true;
      var selectbox1_source = "JciYearCode";
      var selectbox1_destination = "JciYearCodeOptions";
      var selectbox1_url = "api/jci-year/options";
      var selectbox1_conditions_array = {
        UserInterface: 3,
        where: [["jci_year_id", ">=", 15]],
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    searchForm() {
      console.log("searchForm is called");

      var message = "";

      var Category = this.Category;
      console.log({ Category });

      if (Category > 0) {
        this.LoadingFlag = true;
        this.tableData1 = [];
        this.ExcelDownloadFlag = false;
        this.ExcelFileName = "";
        this.ExcelFileFlag = false;
        this.Modules = {};

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/lom-training-question/lists";

        var upload = {
          UserInterface: 2,
          Category: this.Category,
          YearCode: this.JciYearCode,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = {};
        var table = {};
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.LoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            console.log({ records });

            if (flag == 1) {
              var options = records.TableOptions;
              console.log({ options });

              thisIns.tableColumns1 = records.TableHeader;
              thisIns.tableOptions1 = options;
              thisIns.ExcelDownloadFlag = options.ExcelDownloadButtonFlag;
              thisIns.tableData1 = records.TableData;
              thisIns.toast("success", output);
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.LoadingFlag = false;
          });
      } else {
        if (Category == "") {
          message += "Category should not be empty. ";
        }
        this.toast("error", message);
      }
    },
    generateReportExcel() {
      console.log("generateReportExcel is called");

      // var validate1 = this.$refs.form2.validate();
      var FromDate = "2021-08-04";
      var ToDate = "2021-08-05";
      var validate1 = true;
      console.log(
        "validate1=" +
          validate1 +
          ", FromDate=" +
          FromDate +
          ", ToDate=" +
          ToDate
      );

      if (validate1) {
        this.ExcelFileName = "";
        this.ExcelLoadingFlag = true;
        this.ExcelFileFlag = false;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url =
          server_url + "api/reports/directory-information/generate-excel";
        var upload = {
          UserInterface: 1,
          YearCode: this.JciYearCode,
          ZoneCode: this.ZoneCode,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()

            thisIns.ExcelLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              thisIns.ExcelFileName = records.ExcelFileName;
              thisIns.ExcelDownloadFlag = records.ExcelDownloadFlag;
              thisIns.ExcelFileFlag = true;
              thisIns.toast("success", output);
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.ExcelLoadingFlag = false;
          });
      } else {
        var message = "Kindly fill the required fields";
        this.toast("error", message);
      }
    },
    deleteConfirmAlert(tr) {
      console.log("deleteConfirmAlert called");
      var message = "";
      // var tr = this.selected
      // var n1 = tr.length
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));

      if (n1 > 0) {
        var htmlTxt = "";
        htmlTxt += "<br/> Category <b> " + tr.CategoryName + "</b>";
        htmlTxt += "<br/> Question  <b> " + tr.QuestionText + "</b>";
        htmlTxt += "<br/> Question Type <b> " + tr.QuestionTypeTxt + "</b>";

        Swal.fire({
          title: "Do you want to continue?",
          text: "Please verify all the information before proceeding.",
          icon: "warning",
          html: htmlTxt,
          showCancelButton: true,
          confirmButtonText: `Delete`,
          confirmButtonColor: "#d33",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#3085d6",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            this.deleteData(tr.QuestionId);
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        if (n1 == 0) {
          message += "Kindly select one record to delete. ";
        }
        this.sweetAlert("error", message, false);
      }
    },
    deleteData(QuestionId) {
      console.log("deleteData is called");

      console.log("QuestionId=" + QuestionId);

      if (QuestionId > 0) {
        this.DeleteLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/lom-training-question/destroy";
        var upload = {
          UserInterface: 1,
          Question: QuestionId,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = {};
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.DeleteLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            if (flag == 1) {
              thisIns.sweetAlert("success", output, true);
              thisIns.searchForm();
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.LoadingFlag = false;
            thisIns.DeleteLoadingFlag = false;
          });
      } else {
        var message = "Question Id should not be empty. ";
        this.sweetAlert("error", message, false);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>
<style lang="scss">
// Color Palette - JCI Brand Colors
$navy: #3a67b1;
$aqua: #0097d7;
$gold: #edbe38;
$seaform: #56bda3;
$text-off-black: #0a0f29;
$text-white: #ffffff;
$danger-color: #e74c3c;
$border-color: #e2e8f0;
$success-color: #4caf50;

// ============================================
// KEYFRAME ANIMATIONS
// ============================================
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-12px);
  }
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
  100% {
    opacity: 1;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

// ============================================
// MAIN CONTAINER STYLES
// ============================================
#lom-events-list {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding: 30px 15px;

  // ============================================
  // HEADER CARD
  // ============================================
  .header-card {
    background: linear-gradient(
      135deg,
      $navy 0%,
      $aqua 50%,
      $navy 100%
    ) !important;
    border-radius: 20px;
    overflow: hidden;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    border: none;
    box-shadow: 0 20px 60px rgba(58, 103, 177, 0.5),
      0 0 40px rgba(0, 151, 215, 0.3);

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        45deg,
        transparent 30%,
        rgba(255, 255, 255, 0.1) 50%,
        transparent 70%
      );
      animation: shimmer 3s infinite;
    }

    .header-gradient {
      position: relative;
      z-index: 1;
    }

    .header-backdrop-effect {
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.15) 0%,
        transparent 70%
      );
      animation: rotate 25s linear infinite;
    }

    .header-content {
      position: relative;
      z-index: 2;
    }
  }

  .header-icon-box {
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.25) 0%,
      rgba(255, 255, 255, 0.15) 100%
    );
    backdrop-filter: blur(15px);
    border-radius: 18px;
    padding: 14px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.4);
    animation: float 4s ease-in-out infinite;
    border: 2px solid rgba(255, 255, 255, 0.3);
  }

  .header-title {
    font-size: 36px;
    font-weight: 900;
    text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3), 0 0 20px rgba(0, 151, 215, 0.3);
    animation: slideInLeft 0.8s ease-out;
    letter-spacing: 0.8px;
    color: $text-white;
  }

  .header-subtitle {
    font-size: 17px;
    opacity: 0.98;
    animation: slideInLeft 1s ease-out 0.1s backwards;
    letter-spacing: 0.4px;
    color: $text-white;
    font-weight: 500;
  }

  .action-btn {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    background: rgba(255, 255, 255, 0.15) !important;
    border: 2px solid rgba(255, 255, 255, 0.3) !important;
    backdrop-filter: blur(10px);
    color: white !important;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 24px rgba(0, 0, 0, 0.3) !important;
      background: rgba(255, 255, 255, 0.25) !important;
    }
  }

  // ============================================
  // FILTER CARD
  // ============================================
  .filter-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%) !important;
    box-shadow: 0 16px 48px rgba(58, 103, 177, 0.12) !important;
    border: none !important;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s backwards;

    .form-label {
      display: flex;
      align-items: center;
      font-weight: 600;
      font-size: 14px;
      color: $text-off-black;
      margin-bottom: 8px;
      gap: 6px;
    }

    .form-field-wrapper {
      margin-bottom: 0;
    }

    .custom-input {
      border-radius: 8px;
    }

    .search-field {
      border-radius: 8px;

      &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
      }

      ::v-deep input {
        font-size: 14px !important;
      }
    }
  }

  // ============================================
  // TABLE CARD
  // ============================================
  .table-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%) !important;
    box-shadow: 0 16px 48px rgba(58, 103, 177, 0.12) !important;
    border: none !important;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s backwards;
  }

  .table-title {
    font-size: 18px;
    font-weight: 700;
    color: $navy;
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
  }

  .search-field {
    transition: all 0.3s ease;
    border-radius: 8px;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
    }

    &::v-deep input {
      font-size: 14px !important;
    }
  }

  .questions-table {
    &::v-deep {
      .v-data-table__wrapper {
        border-radius: 8px;
        overflow: hidden;
        min-height: 500px;
      }

      thead {
        background: linear-gradient(135deg, $navy 0%, $aqua 100%) !important;

        th {
          color: white !important;
          font-weight: 700 !important;
          font-size: 20px !important;
          text-transform: uppercase !important;
          letter-spacing: 0.6px !important;
          padding: 35px 28px !important;
          border: none !important;
          background: linear-gradient(135deg, $navy 0%, $aqua 100%) !important;
          height: 95px !important;
        }
      }

      tbody {
        tr {
          border-bottom: 1px solid $border-color;
          transition: all 0.3s ease;
          height: 100px !important;

          &:hover {
            background: linear-gradient(
              90deg,
              rgba(0, 151, 215, 0.12) 0%,
              transparent 100%
            ) !important;
            box-shadow: inset 0 0 0 1px rgba(0, 151, 215, 0.15);
          }

          td {
            padding: 30px 28px !important;
            color: $text-off-black;
            font-size: 20px !important;
            font-weight: 500;
            line-height: 2;
            display: flex !important;
            align-items: center !important;
            vertical-align: middle !important;
          }
        }

        tr:nth-child(even) {
          background: #f8f9fc;
        }
      }

      .v-data-table__checkbox {
        margin-right: 12px;
      }
    }
  }

  // ============================================
  // STATUS CHIPS & ACTION BUTTONS
  // ============================================
  .action-cell {
    display: flex;
    gap: 6px;
    align-items: center;
  }

  .action-icon-btn {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    background: linear-gradient(
      135deg,
      rgba(0, 0, 0, 0.05),
      rgba(0, 0, 0, 0.02)
    ) !important;

    &:hover {
      transform: scale(1.15) !important;
      background: linear-gradient(
        135deg,
        rgba(0, 0, 0, 0.1),
        rgba(0, 0, 0, 0.05)
      ) !important;
    }
  }

  // ============================================
  // EMPTY STATE
  // ============================================
  .empty-state-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%) !important;
    box-shadow: 0 16px 48px rgba(58, 103, 177, 0.12) !important;
    border: none !important;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s backwards;

    &::v-deep {
      .v-icon {
        animation: pulse 2s ease-in-out infinite;
      }
    }
  }

  // ============================================
  // RESPONSIVE DESIGN
  // ============================================
  @media (max-width: 960px) {
    padding: 20px 12px;

    .header-title {
      font-size: 28px;
    }

    .header-subtitle {
      font-size: 14px;
    }

    .header-icon-box {
      padding: 10px;
    }

    .table-title {
      font-size: 16px;
    }
  }

  @media (max-width: 600px) {
    padding: 16px 10px;

    .header-card {
      margin-bottom: 16px;

      .header-content {
        padding: 16px !important;
      }
    }

    .header-title {
      font-size: 22px;
    }

    .header-subtitle {
      font-size: 13px;
    }

    .search-field {
      max-width: 100% !important;
      margin-top: 12px;
    }

    .questions-table {
      &::v-deep {
        font-size: 12px;

        th,
        td {
          padding: 8px 6px !important;
        }
      }
    }

    .action-cell {
      flex-direction: column;
      gap: 3px;
    }
  }
}
</style>